/**
 * Contrato de las entidades de ElDescuadre.
 *
 * Los nombres de campo son **exactamente** los mismos en el backend, la web y
 * las apps móviles, porque todos importan este archivo. No hay traducciones de
 * nombres por el camino, que es lo que hacía que en Cuentia se perdieran datos.
 */
import { z } from 'zod';
import { zColor, zCurrency, zDateISO, zEmail, zInstantISO, zMonthKey, zNonEmptyText, zNonNegativePesos, zOptionalText, zPesos, zPositivePesos, zQueryBoolean, zTimeHHmm, zUuid, strictObject, } from './common.js';
// ---------------------------------------------------------------------------
//  Enumeraciones (espejo exacto de las de la base de datos)
// ---------------------------------------------------------------------------
export const zAccountKind = z.enum([
    'CASH',
    'SAVINGS',
    'WALLET',
    'CREDIT_CARD',
    'FIADO',
    'LOAN',
    'INVESTMENT',
]);
export const zCategoryKind = z.enum(['EXPENSE', 'INCOME']);
export const zTransactionKind = z.enum([
    'EXPENSE',
    'INCOME',
    'TRANSFER',
    'FIADO_PURCHASE',
    'FIADO_PAYMENT',
    'DEBT_PAYMENT',
    'DEBT_INTEREST',
    'SPLIT_SETTLEMENT',
    'ADJUSTMENT',
]);
export const zTransactionSource = z.enum(['MANUAL', 'RECURRING', 'IMPORT', 'RECEIPT', 'SYSTEM']);
export const zDebtKind = z.enum([
    'CREDIT_CARD',
    'PERSONAL_LOAN',
    'PAYROLL_LOAN',
    'VEHICLE',
    'MORTGAGE',
    'STORE_CREDIT',
    'OTHER',
]);
export const zDebtStatus = z.enum(['CURRENT', 'FROZEN_ARREARS', 'NEGOTIATION', 'PAID']);
export const zRecurrenceFrequency = z.enum([
    'WEEKLY',
    'BIWEEKLY',
    'MONTHLY',
    'BIMONTHLY',
    'QUARTERLY',
    'SEMIANNUAL',
    'YEARLY',
]);
export const zSplitStatus = z.enum(['PENDING', 'PARTIAL', 'SETTLED', 'CANCELLED']);
export const zGoalStatus = z.enum(['ACTIVE', 'ACHIEVED', 'PAUSED', 'CANCELLED']);
export const zFiadoEntryKind = z.enum(['PURCHASE', 'PAYMENT']);
export const zTheme = z.enum(['LIGHT', 'DARK', 'SYSTEM']);
// ---------------------------------------------------------------------------
//  Autenticación y usuarios
// ---------------------------------------------------------------------------
/**
 * Requisitos mínimos de contraseña, comprobados en el servidor.
 *
 * **8 caracteres**, no más. Se empezó con 10 y fue un error: lo único que consigue un
 * mínimo alto es que la gente acabe poniendo `Contraseña1` o apuntándola en un papel.
 * Con 8 se cubre de sobra a quien use un gestor de contraseñas, y no estorba a quien
 * escriba la suya.
 *
 * Se mantiene exigir una letra y un número porque no molesta a nadie y descarta de
 * golpe las contraseñas del tipo `12345678`, que son las primeras que prueba cualquiera.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const zPassword = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`)
    .max(200, 'La contraseña es demasiado larga')
    .refine((v) => /[a-zA-Z]/.test(v), 'La contraseña debe incluir al menos una letra')
    .refine((v) => /[0-9]/.test(v), 'La contraseña debe incluir al menos un número');
export const zRegisterRequest = strictObject({
    email: zEmail,
    password: zPassword,
    displayName: zNonEmptyText(120, 'nombre'),
    /** Aceptación de la política de datos (Ley 1581 de Colombia). */
    acceptPrivacy: z.literal(true, {
        errorMap: () => ({ message: 'Debes aceptar la política de tratamiento de datos' }),
    }),
});
export const zLoginRequest = strictObject({
    email: zEmail,
    password: z.string().min(1, 'La contraseña es obligatoria').max(200),
});
export const zForgotPasswordRequest = strictObject({ email: zEmail });
export const zResetPasswordRequest = strictObject({
    token: zNonEmptyText(400, 'token'),
    password: zPassword,
});
export const zChangePasswordRequest = strictObject({
    currentPassword: z.string().min(1).max(200),
    newPassword: zPassword,
});
// ---------------------------------------------------------------------------
//  Entrar con la huella o con la cara (WebAuthn)
// ---------------------------------------------------------------------------
/**
 * La respuesta que devuelve el dispositivo al vincularse.
 *
 * **No se valida campo a campo a propósito.** Su forma la define el estándar WebAuthn y
 * cambia entre navegadores y versiones; describirla aquí sería una fuente de fallos
 * cada vez que uno de ellos añadiera algo. Lo que se comprueba de verdad —que la firma
 * corresponda al desafío, al dominio y a la credencial— lo hace la librería del
 * servidor, que es la que sabe. Aquí solo se exige que sea un objeto.
 */
const zWebAuthnResponse = z.record(z.unknown());
export const zWebAuthnRegisterVerify = strictObject({
    response: zWebAuthnResponse,
    label: z.string().trim().max(120).default('Este dispositivo'),
});
/**
 * Para pedir las opciones de entrada.
 *
 * El correo es opcional: sin él, el dispositivo dice con qué credenciales cuenta y el
 * servidor busca las suyas. **Así no se puede averiguar qué correos están registrados**
 * probando en esta pantalla.
 */
export const zWebAuthnLoginOptions = strictObject({
    email: zEmail.optional(),
});
export const zWebAuthnLoginVerify = strictObject({
    response: zWebAuthnResponse,
});
/** Una credencial vinculada, para poder verla y borrarla desde los ajustes. */
export const zWebAuthnCredential = strictObject({
    id: zUuid,
    label: z.string(),
    deviceType: z.string(),
    backedUp: z.boolean(),
    lastUsedAt: zInstantISO.nullable(),
    createdAt: zInstantISO,
});
export const zUserPublic = strictObject({
    id: zUuid,
    email: zEmail,
    displayName: z.string(),
    role: z.enum(['OWNER', 'ADMIN', 'MEMBER']),
    timezone: z.string(),
    locale: z.string(),
    emailVerified: z.boolean(),
    createdAt: zInstantISO,
});
/**
 * Respuesta de autenticación.
 *
 * Fíjate en lo que **no** aparece: el token de refresco. Viaja en una cookie
 * HttpOnly que el navegador no puede leer desde JavaScript, así que un script
 * inyectado no puede robarlo. Guardarlo en `localStorage` —como hacían Cuentify
 * y Cuentia— lo dejaría al alcance de cualquier XSS.
 */
export const zAuthResponse = strictObject({
    /** Token de acceso de vida corta. La web lo mantiene solo en memoria. */
    accessToken: z.string(),
    /** Segundos de vida del token de acceso. */
    expiresIn: z.number().int().positive(),
    user: zUserPublic,
});
/** Respuesta del cierre de sesión. */
export const zOkResponse = strictObject({
    ok: z.literal(true),
    message: z.string().optional(),
});
// ---------------------------------------------------------------------------
//  Cuentas
// ---------------------------------------------------------------------------
export const zAccountCreate = strictObject({
    name: zNonEmptyText(120, 'nombre'),
    kind: zAccountKind,
    initialBalance: zPesos.default(0),
    creditLimit: zNonNegativePesos.nullish(),
    cutoffDay: z.number().int().min(1).max(28).nullish(),
    paymentDueDay: z.number().int().min(1).max(28).nullish(),
    fiadoLimit: zNonNegativePesos.nullish(),
    currency: zCurrency.default('COP'),
    color: zColor.default('#20A9E6'),
    icon: z.string().max(40).default('Wallet'),
    institution: zOptionalText(120),
    maskedNumber: zOptionalText(30),
    order: z.number().int().min(0).default(0),
});
export const zAccountUpdate = strictObject({
    name: zNonEmptyText(120, 'nombre').optional(),
    kind: zAccountKind.optional(),
    initialBalance: zPesos.optional(),
    creditLimit: zNonNegativePesos.nullish(),
    cutoffDay: z.number().int().min(1).max(28).nullish(),
    paymentDueDay: z.number().int().min(1).max(28).nullish(),
    fiadoLimit: zNonNegativePesos.nullish(),
    color: zColor.optional(),
    icon: z.string().max(40).optional(),
    institution: zOptionalText(120),
    maskedNumber: zOptionalText(30),
    isArchived: z.boolean().optional(),
    order: z.number().int().min(0).optional(),
});
export const zAccount = strictObject({
    id: zUuid,
    name: z.string(),
    kind: zAccountKind,
    initialBalance: zPesos,
    /** Saldo real, siempre derivado de los movimientos. */
    balance: zPesos,
    /** Saldo contable incluyendo el saldo inicial. */
    creditLimit: zPesos.nullable(),
    cutoffDay: z.number().int().nullable(),
    paymentDueDay: z.number().int().nullable(),
    fiadoLimit: zPesos.nullable(),
    currency: z.string(),
    color: z.string(),
    icon: z.string(),
    institution: z.string().nullable(),
    maskedNumber: z.string().nullable(),
    isArchived: z.boolean(),
    order: z.number().int(),
    /** Para tarjetas: cupo usado y disponible. */
    usedCredit: zPesos.nullable(),
    availableCredit: zPesos.nullable(),
    createdAt: zInstantISO,
    updatedAt: zInstantISO,
});
// ---------------------------------------------------------------------------
//  Categorías
// ---------------------------------------------------------------------------
export const zCategory = strictObject({
    id: zUuid,
    code: z.string().nullable(),
    parentId: zUuid.nullable(),
    name: z.string(),
    hint: z.string().nullable(),
    kind: zCategoryKind,
    order: z.number().int(),
    color: z.string(),
    icon: z.string(),
    isSystem: z.boolean(),
    isActive: z.boolean(),
});
export const zCategoryCreate = strictObject({
    name: zNonEmptyText(120, 'nombre'),
    hint: zOptionalText(300),
    kind: zCategoryKind,
    parentId: zUuid.nullish(),
    order: z.number().int().min(0).default(0),
    color: zColor.default('#20A9E6'),
    icon: z.string().max(40).default('Tag'),
});
// ---------------------------------------------------------------------------
//  Movimientos
// ---------------------------------------------------------------------------
export const zTransactionItem = strictObject({
    id: zUuid.optional(),
    categoryId: zUuid.nullish(),
    amount: zPositivePesos,
    description: zNonEmptyText(200, 'descripción'),
});
export const zCashDirection = z.enum(['IN', 'OUT']);
export const zTransactionCreate = strictObject({
    kind: zTransactionKind,
    amount: zPositivePesos,
    /**
     * Solo para los ajustes de saldo: indica si el dinero entra o sale. En el
     * resto de tipos la dirección la determina el propio tipo de movimiento.
     */
    direction: zCashDirection.nullish(),
    currency: zCurrency.default('COP'),
    originalAmount: zPositivePesos.nullish(),
    fxRate: z.number().positive().nullish(),
    fxSource: zOptionalText(64),
    /** Fecha y hora locales de Colombia. El servidor las convierte a UTC. */
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
    accountId: zUuid.nullish(),
    toAccountId: zUuid.nullish(),
    categoryId: zUuid.nullish(),
    merchant: zOptionalText(160),
    notes: zOptionalText(2000),
    fiadoStoreId: zUuid.nullish(),
    debtId: zUuid.nullish(),
    splitId: zUuid.nullish(),
    receiptId: zUuid.nullish(),
    /** Desglose en varios artículos. La suma debe igualar el importe total. */
    items: z.array(zTransactionItem).max(100).optional(),
    tagIds: z.array(zUuid).max(20).optional(),
    /**
     * Clave de idempotencia generada por el cliente. Evita duplicar un gasto al
     * reintentar sin señal. También se acepta en la cabecera `Idempotency-Key`.
     */
    idempotencyKey: zOptionalText(120),
});
export const zTransactionUpdate = strictObject({
    amount: zPositivePesos.optional(),
    currency: zCurrency.optional(),
    originalAmount: zPositivePesos.nullish(),
    fxRate: z.number().positive().nullish(),
    date: zDateISO.optional(),
    time: zTimeHHmm.optional(),
    accountId: zUuid.nullish(),
    toAccountId: zUuid.nullish(),
    categoryId: zUuid.nullish(),
    merchant: zOptionalText(160),
    notes: zOptionalText(2000),
    items: z.array(zTransactionItem).max(100).optional(),
    tagIds: z.array(zUuid).max(20).optional(),
});
export const zTransaction = strictObject({
    id: zUuid,
    kind: zTransactionKind,
    amount: zPesos,
    /** Solo en ajustes de saldo. */
    direction: zCashDirection.nullable(),
    currency: z.string(),
    originalAmount: zPesos.nullable(),
    fxRate: z.number().nullable(),
    occurredAt: zInstantISO,
    /** Fecha local en Colombia, para agrupar sin errores de zona horaria. */
    date: zDateISO,
    time: zTimeHHmm,
    accountId: zUuid.nullable(),
    toAccountId: zUuid.nullable(),
    categoryId: zUuid.nullable(),
    merchant: z.string().nullable(),
    notes: z.string().nullable(),
    fiadoStoreId: zUuid.nullable(),
    debtId: zUuid.nullable(),
    splitId: zUuid.nullable(),
    receiptId: zUuid.nullable(),
    items: z.array(zTransactionItem),
    tagIds: z.array(zUuid),
    source: zTransactionSource,
    version: z.number().int(),
    /** Fecha en que se envió a la papelera. `null` si está vigente. */
    deletedAt: zInstantISO.nullable(),
    createdAt: zInstantISO,
    updatedAt: zInstantISO,
});
/**
 * Una lista de identificadores para operar en lote.
 *
 * Se pone un tope de 500 y no de 10.000: el borrado en lote es para limpiar la pantalla
 * después de marcar unas cuantas casillas, no para vaciar el historial entero. Y una
 * lista enorme desde el navegador es, además, una petición que tarda y que puede
 * quedarse a medias.
 */
export const zBulkIds = strictObject({
    ids: z.array(zUuid).min(1, 'Hay que elegir al menos uno').max(500, 'Son demasiados de una vez'),
});
export const zTransactionFilter = strictObject({
    from: zDateISO.optional(),
    to: zDateISO.optional(),
    monthKey: zMonthKey.optional(),
    kinds: z.array(zTransactionKind).optional(),
    categoryIds: z.array(zUuid).optional(),
    accountIds: z.array(zUuid).optional(),
    tagIds: z.array(zUuid).optional(),
    search: z.string().trim().max(120).optional(),
    minAmount: zPesos.optional(),
    maxAmount: zPesos.optional(),
    includeDeleted: zQueryBoolean.default(false),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
// ---------------------------------------------------------------------------
//  Presupuestos
// ---------------------------------------------------------------------------
export const zBudgetUpsert = strictObject({
    categoryId: zUuid,
    monthKey: zMonthKey,
    amount: zNonNegativePesos,
    rollover: z.boolean().default(false),
    notes: zOptionalText(500),
});
export const zBudget = strictObject({
    id: zUuid,
    categoryId: zUuid,
    monthKey: zMonthKey,
    amount: zPesos,
    rollover: z.boolean(),
    rolledOver: zPesos,
    /** Total disponible = importe + arrastre. */
    available: zPesos,
    spent: zPesos,
    remaining: zPesos,
    percentUsed: z.number(),
    notes: z.string().nullable(),
});
/** Ritmo de gasto del mes: responde a «¿voy bien o se me va?». */
export const zBudgetPacing = strictObject({
    categoryId: zUuid,
    monthKey: zMonthKey,
    amount: zPesos,
    spent: zPesos,
    remaining: zPesos,
    daysInMonth: z.number().int(),
    daysElapsed: z.number().int(),
    daysRemaining: z.number().int(),
    /** Gasto medio por día hasta hoy. */
    dailyRate: z.number(),
    /** Gasto por día que te permitirías para llegar a fin de mes. */
    allowedDailyRate: z.number(),
    /** Días que faltan para agotar el presupuesto al ritmo actual. `null` si no gastas. */
    daysUntilExhausted: z.number().int().nullable(),
    /** true si al ritmo actual superarás el presupuesto. */
    onTrackToExceed: z.boolean(),
    percentUsed: z.number(),
});
// ---------------------------------------------------------------------------
//  Deudas
// ---------------------------------------------------------------------------
export const zDebtCreate = strictObject({
    entity: zNonEmptyText(160, 'entidad'),
    kind: zDebtKind,
    balance: zNonNegativePesos.default(0),
    totalLimit: zNonNegativePesos.nullish(),
    usedLimit: zNonNegativePesos.nullish(),
    rateEA: z.number().min(0).max(1000).nullish(),
    rateMV: z.number().min(0).max(1000).nullish(),
    installment: zNonNegativePesos.nullish(),
    cutoffDay: z.number().int().min(1).max(28).nullish(),
    dueDay: z.number().int().min(1).max(28).nullish(),
    status: zDebtStatus.default('CURRENT'),
    priority: z.number().int().min(1).max(5).default(1),
    arrearsMonths: z.number().int().min(0).default(0),
    settlementTarget: zNonNegativePesos.nullish(),
    accountId: zUuid.nullish(),
    notes: zOptionalText(2000),
});
export const zDebtUpdate = zDebtCreate.partial();
export const zDebt = strictObject({
    id: zUuid,
    entity: z.string(),
    kind: zDebtKind,
    balance: zPesos,
    totalLimit: zPesos.nullable(),
    usedLimit: zPesos.nullable(),
    rateEA: z.number().nullable(),
    rateMV: z.number().nullable(),
    installment: zPesos.nullable(),
    cutoffDay: z.number().int().nullable(),
    dueDay: z.number().int().nullable(),
    status: zDebtStatus,
    priority: z.number().int(),
    arrearsMonths: z.number().int(),
    settlementTarget: zPesos.nullable(),
    settlementNote: z.string().nullable(),
    accountId: zUuid.nullable(),
    notes: z.string().nullable(),
    createdAt: zInstantISO,
    updatedAt: zInstantISO,
});
export const zDebtPaymentCreate = strictObject({
    debtId: zUuid,
    /** Parte que baja el capital. */
    principal: zNonNegativePesos.default(0),
    /** Intereses del período: sí cuentan como gasto (categoría 7). */
    interest: zNonNegativePesos.default(0),
    insurance: zNonNegativePesos.default(0),
    fees: zNonNegativePesos.default(0),
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
    accountId: zUuid,
    notes: zOptionalText(500),
});
// ---------------------------------------------------------------------------
//  Fiado de barrio
// ---------------------------------------------------------------------------
export const zFiadoStoreCreate = strictObject({
    name: zNonEmptyText(120, 'nombre de la tienda'),
    ownerName: zOptionalText(120),
    phone: zOptionalText(30),
    limit: zNonNegativePesos.nullish(),
    accountId: zUuid.nullish(),
    notes: zOptionalText(1000),
});
export const zFiadoStoreUpdate = zFiadoStoreCreate.partial();
export const zFiadoStore = strictObject({
    id: zUuid,
    name: z.string(),
    ownerName: z.string().nullable(),
    phone: z.string().nullable(),
    limit: zPesos.nullable(),
    balance: zPesos,
    available: zPesos.nullable(),
    accountId: zUuid.nullable(),
    notes: z.string().nullable(),
    /** true si se superó el tope acordado con el tendero. */
    overLimit: z.boolean(),
    createdAt: zInstantISO,
    updatedAt: zInstantISO,
});
export const zFiadoEntryCreate = strictObject({
    storeId: zUuid,
    kind: zFiadoEntryKind,
    amount: zPositivePesos,
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
    /** Desglose por bolsillo: comida, mecato y licor. */
    marketAmount: zNonNegativePesos.default(0),
    snackAmount: zNonNegativePesos.default(0),
    leisureAmount: zNonNegativePesos.default(0),
    items: z.array(zTransactionItem).max(100).optional(),
    notes: zOptionalText(500),
    /** Obligatorio en los abonos: de qué cuenta sale el dinero. */
    accountId: zUuid.nullish(),
    idempotencyKey: zOptionalText(120),
});
export const zFiadoEntry = strictObject({
    id: zUuid,
    storeId: zUuid,
    kind: zFiadoEntryKind,
    amount: zPesos,
    balanceAfter: zPesos,
    occurredAt: zInstantISO,
    date: zDateISO,
    marketAmount: zPesos,
    snackAmount: zPesos,
    leisureAmount: zPesos,
    notes: z.string().nullable(),
    transactionId: zUuid.nullable(),
});
// ---------------------------------------------------------------------------
//  Recurrentes y suscripciones
// ---------------------------------------------------------------------------
export const zRecurringCreate = strictObject({
    description: zNonEmptyText(200, 'descripción'),
    amount: zPositivePesos,
    categoryId: zUuid.nullish(),
    accountId: zUuid.nullish(),
    frequency: zRecurrenceFrequency.default('MONTHLY'),
    /** Día del mes (1-31) o día de la semana (0-6) según la frecuencia. */
    day: z.number().int().min(0).max(31).nullish(),
    startDate: zDateISO,
    endDate: zDateISO.nullish(),
    isPaused: z.boolean().default(false),
    remindDaysBefore: z.number().int().min(0).max(30).nullish(),
    merchant: zOptionalText(160),
    notes: zOptionalText(500),
});
export const zRecurringUpdate = zRecurringCreate.partial();
export const zRecurringRule = strictObject({
    id: zUuid,
    description: z.string(),
    amount: zPesos,
    categoryId: zUuid.nullable(),
    accountId: zUuid.nullable(),
    frequency: zRecurrenceFrequency,
    day: z.number().int().nullable(),
    startDate: zDateISO,
    endDate: zDateISO.nullable(),
    isPaused: z.boolean(),
    remindDaysBefore: z.number().int().nullable(),
    lastAmount: zPesos.nullable(),
    /** Aviso de «suscripción zombi»: subió de precio respecto al cobro anterior. */
    priceIncreased: z.boolean(),
    lastRunAt: zInstantISO.nullable(),
    nextRunAt: zInstantISO.nullable(),
    /** Próxima fecha de cobro en hora de Colombia. */
    nextRunDate: zDateISO.nullable(),
    daysUntilNextRun: z.number().int().nullable(),
    merchant: z.string().nullable(),
    notes: z.string().nullable(),
});
// ---------------------------------------------------------------------------
//  Gastos compartidos
// ---------------------------------------------------------------------------
export const zSplitCreate = strictObject({
    description: zNonEmptyText(200, 'descripción'),
    total: zPositivePesos,
    myShare: zNonNegativePesos,
    participants: z.number().int().min(2).max(50).default(2),
    reimbursable: z.boolean().default(false),
    isCompanyExpense: z.boolean().default(false),
    debtorName: zOptionalText(120),
    debtorContact: zOptionalText(120),
    dueDate: zDateISO.nullish(),
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
    accountId: zUuid.nullish(),
    categoryId: zUuid.nullish(),
    transactionId: zUuid.nullish(),
});
export const zSplitSettle = strictObject({
    amount: zPositivePesos,
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
    accountId: zUuid,
    notes: zOptionalText(500),
});
export const zSplit = strictObject({
    id: zUuid,
    description: z.string(),
    total: zPesos,
    myShare: zPesos,
    receivable: zPesos,
    settledAmount: zPesos,
    reimbursable: z.boolean(),
    isCompanyExpense: z.boolean(),
    debtorName: z.string().nullable(),
    debtorContact: z.string().nullable(),
    status: zSplitStatus,
    participants: z.number().int(),
    dueDate: zDateISO.nullable(),
    settledAt: zInstantISO.nullable(),
    occurredAt: zInstantISO,
});
// ---------------------------------------------------------------------------
//  Metas de ahorro
// ---------------------------------------------------------------------------
export const zGoalCreate = strictObject({
    name: zNonEmptyText(160, 'nombre'),
    targetAmount: zPositivePesos,
    targetDate: zDateISO.nullish(),
    accountId: zUuid.nullish(),
    categoryId: zUuid.nullish(),
    color: z.string().max(9).default('#ECD082'),
    icon: z.string().max(40).default('Target'),
    notes: zOptionalText(1000),
});
export const zGoalUpdate = zGoalCreate.partial().extend({
    status: zGoalStatus.optional(),
});
export const zGoalContributionCreate = strictObject({
    goalId: zUuid,
    amount: zPositivePesos,
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
    accountId: zUuid.nullish(),
    notes: zOptionalText(500),
});
export const zGoal = strictObject({
    id: zUuid,
    name: z.string(),
    targetAmount: zPesos,
    savedAmount: zPesos,
    remaining: zPesos,
    percentSaved: z.number(),
    targetDate: zDateISO.nullable(),
    accountId: zUuid.nullable(),
    categoryId: zUuid.nullable(),
    status: zGoalStatus,
    color: z.string(),
    icon: z.string(),
    notes: z.string().nullable(),
    /** Aporte mensual necesario para llegar a la fecha objetivo. */
    requiredMonthlyAmount: zPesos.nullable(),
    achievedAt: zInstantISO.nullable(),
});
// ---------------------------------------------------------------------------
//  Recibos
// ---------------------------------------------------------------------------
export const zReceiptSuggestRequest = strictObject({
    fileName: zOptionalText(300),
    /** Texto ya extraído de la imagen, en el dispositivo. El servidor no ve la foto. */
    hintText: zOptionalText(20000),
});
/**
 * Un artículo propuesto a partir del texto del recibo.
 *
 * `confidence` no es una medida inventada: refleja cuánto se pudo leer. Un artículo
 * con poca confianza se enseña marcado para que el usuario lo revise.
 */
export const zReceiptItemSuggestion = strictObject({
    description: z.string(),
    amount: zPesos,
    categoryId: zUuid.nullish(),
    /**
     * De dónde salió la categoría, para poder decírselo al usuario:
     *
     *  · `MEMORY` — de lo que él mismo enseñó antes. Es lo más fiable.
     *  · `GUESS`  — de la lista de palabras. Puede equivocarse.
     *  · `NONE`   — no se sabe. **Hay que preguntarle.**
     *
     * Sin esto, la pantalla no puede distinguir «esto lo sé porque tú me lo dijiste» de
     * «esto me lo estoy inventando», y las dos cosas merecen confianza muy distinta.
     */
    source: z.enum(['MEMORY', 'GUESS', 'NONE']).default('NONE'),
    confidence: z.number().min(0).max(1),
});
/**
 * Lo que se entendió del recibo, **antes** de crear nada.
 *
 * No lleva identificador de recibo porque no hace falta: el análisis se puede hacer
 * sobre un texto sin haber subido ninguna foto, y el usuario revisa la propuesta
 * antes de que exista un movimiento.
 */
export const zReceiptSuggestion = strictObject({
    merchant: z.string().nullable(),
    amount: zPesos.nullable(),
    confidence: z.number().min(0).max(1),
    items: z.array(zReceiptItemSuggestion),
    /** Qué hay que revisar. `null` cuando todo cuadra. */
    warning: z.string().nullable(),
    rawText: z.string(),
});
/** Un recibo guardado. */
export const zReceipt = strictObject({
    id: zUuid,
    fileName: z.string().nullable(),
    mimeType: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    merchant: z.string().nullable(),
    amount: zPesos.nullable(),
    confidence: z.number().nullable(),
    items: z.array(zReceiptItemSuggestion),
    rawText: z.string().nullable(),
    warning: z.string().nullable(),
    /** Motor que lo leyó: «tesseract» en el dispositivo, o nulo si se escribió a mano. */
    engine: z.string().nullable(),
    /** Cuándo se confirmó y se convirtió en un movimiento. `null` si está pendiente. */
    confirmedAt: zInstantISO.nullable(),
    createdAt: zInstantISO,
    /** Dirección para ver la imagen. Requiere sesión. */
    imageUrl: z.string(),
});
/** Subir la foto de un recibo. */
export const zReceiptCreateRequest = strictObject({
    /** La imagen en base64, con o sin el prefijo de dirección de datos. */
    contentBase64: z.string().min(1),
    mimeType: z.string().trim().min(3).max(100),
    fileName: zOptionalText(300),
    merchant: zOptionalText(160),
    amount: zNonNegativePesos.nullish(),
    items: z.array(zReceiptItemSuggestion).max(200).optional(),
    rawText: zOptionalText(20000),
    engine: zOptionalText(60),
    confidence: z.number().min(0).max(1).nullish(),
});
/** Confirmar el recibo y crear el movimiento. */
export const zReceiptConfirmRequest = strictObject({
    kind: zTransactionKind,
    amount: zPositivePesos,
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
    accountId: zUuid.nullish(),
    categoryId: zUuid.nullish(),
    merchant: zOptionalText(160),
    notes: zOptionalText(2000),
    /** El desglose revisado por el usuario. La suma tiene que dar el importe. */
    items: z.array(zTransactionItem).max(100).optional(),
    idempotencyKey: zOptionalText(120),
});
// ---------------------------------------------------------------------------
//  Ajustes
// ---------------------------------------------------------------------------
export const zSettingsUpdate = strictObject({
    theme: zTheme.optional(),
    stealthMode: z.boolean().optional(),
    weekStartsOn: z.number().int().min(0).max(6).optional(),
    hormigaDailyBudget: zNonNegativePesos.optional(),
    budgetRollover: z.boolean().optional(),
    trmSource: z.string().max(64).optional(),
    features: z
        .object({
        ocr: z.boolean(),
        copilot: z.boolean(),
        reconciliation: z.boolean(),
        push: z.boolean(),
    })
        .partial()
        .optional(),
});
export const zSettings = strictObject({
    theme: zTheme,
    stealthMode: z.boolean(),
    weekStartsOn: z.number().int(),
    hormigaDailyBudget: zPesos,
    budgetRollover: z.boolean(),
    trmSource: z.string(),
    features: z.object({
        ocr: z.boolean(),
        copilot: z.boolean(),
        reconciliation: z.boolean(),
        push: z.boolean(),
    }),
    privacyAcceptedAt: zInstantISO.nullable(),
    onboardingCompletedAt: zInstantISO.nullable(),
});
//# sourceMappingURL=entities.js.map